import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SnapshotSection } from "@/components/command/WeeklyReviewSnapshot";
import { WR_SECTIONS, weekRange, fmtDate, gbp } from "@/data/weeklyReviewOptions";

const WORDMARK = "/commando-mascot-v2.webp";

export default function WeeklyReviewPrint() {
  const { id } = useParams();
  const [rev, setRev] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.weeklyReview(id).then((r) => { setRev(r); setTimeout(() => window.print(), 600); }).catch(() => setErr(true));
  }, [id]);

  if (err) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Could not load this review.</div>;
  if (!rev) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Preparing printable review…</div>;

  const snap = rev.snapshot || {};
  const ov = snap.overview || {};

  return (
    <div data-testid="wr-print" className="wr-print mx-auto max-w-[900px] bg-white px-10 py-8 text-[#14245F]">
      <style>{`
        @media print { @page { margin: 14mm; } .wr-print { padding: 0 !important; } .wr-section { break-inside: avoid; } button { display:none !important; } }
        body { background: white; }
        .wr-banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      `}</style>

      {/* Branded cover banner */}
      <div className="wr-banner wr-section mb-6 overflow-hidden rounded-2xl" style={{ background: "linear-gradient(115deg,#0B1E5B 0%,#14245F 52%,#1D4ED8 135%)" }}>
        <div className="flex items-stretch justify-between gap-4">
          <div className="p-6 text-white sm:p-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#67E8F9]">Edju Cat · War Room</p>
            <h1 className="mt-1.5 font-heading text-3xl font-bold leading-tight">Founders' Weekly Review</h1>
            <p className="mt-1 text-lg font-semibold text-[#D6DEF6]">Week commencing {weekRange(rev.week_start, rev.week_end)}</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#B9C6EA]">
              <span>Status: <b className="text-white">{rev.status}</b></span>
              <span>Prepared by {rev.prepared_by || "—"}</span>
              {rev.review_meeting_date && <span>Meeting {fmtDate(rev.review_meeting_date)}</span>}
              {rev.attendees && <span>Attendees: {rev.attendees}</span>}
              {rev.finalised_at && <span>Finalised {fmtDate(rev.finalised_at)}</span>}
            </div>
          </div>
          <div className="flex w-[150px] shrink-0 items-end justify-center">
            <img src={WORDMARK} alt="Edju Cat in commando-style War Room uniform" className="h-[160px] w-auto object-contain object-bottom drop-shadow-[0_8px_20px_rgba(0,0,0,0.35)]" />
          </div>
        </div>
      </div>

      {/* Overview */}
      <div className="mb-6 grid grid-cols-4 gap-3">
        {[["Launch readiness", `${ov.launch_readiness ?? 0}%`], ["Priorities completed", ov.priorities_completed ?? 0], ["Overdue items", ov.overdue_items ?? 0], ["Open blockers", ov.open_blockers ?? 0], ["Critical risks", ov.critical_risks ?? 0], ["Available cash", gbp(ov.available_cash)], ["Estimated runway", ov.runway_label || "—"], ["Investor follow-ups due", ov.investor_followups_due ?? 0]].map(([l, v], i) => (
          <div key={l} className="rounded-lg border border-[#E4E9F3] px-3 py-2">
            <p className="text-[9px] font-bold uppercase tracking-wide text-[#5A6B8C]">{l}</p>
            <p className="font-numeric text-lg font-bold">{v}</p>
          </div>
        ))}
      </div>

      {/* Assessment */}
      <div className="wr-section mb-5 rounded-lg bg-[#F7F9FE] p-4">
        <h2 className="mb-2 font-heading text-base font-bold text-[#1D4ED8]">Weekly Assessment</h2>
        {[["Overall assessment", rev.overall_assessment], ["Main achievements", rev.main_achievements], ["Main concerns", rev.main_concerns], ["Founder notes", rev.founder_notes]].map(([l, v], i) => (
          <p key={l} className="mb-1.5 text-sm"><span className="font-semibold">{l}:</span> {v || "—"}</p>
        ))}
      </div>

      {/* Sections */}
      {WR_SECTIONS.map((sec) => {
        const n = (rev.section_notes || {})[sec.key] || {};
        const comments = (rev.comments || []).filter((c) => c.section === sec.key);
        const hasNotes = n.went_well || n.went_badly || n.action_required;
        return (
          <div key={sec.key} className="wr-section mb-5">
            <h2 className="mb-2 border-b border-[#E4E9F3] pb-1 font-heading text-base font-bold" style={{ color: sec.accent }}>{sec.label}</h2>
            <SnapshotSection sectionKey={sec.key} snap={snap} />
            {hasNotes && (
              <div className="mt-2 rounded-lg border border-[#E4E9F3] p-3 text-sm">
                {n.went_well && <p className="mb-1"><span className="font-semibold text-[#16A34A]">Went well:</span> {n.went_well}</p>}
                {n.went_badly && <p className="mb-1"><span className="font-semibold text-[#DC2626]">Did not go well:</span> {n.went_badly}</p>}
                {n.action_required && <p><span className="font-semibold text-[#C2410C]">Action:</span> {n.action_required}{n.action_owner ? ` · ${n.action_owner}` : ""}{n.action_deadline ? ` · due ${n.action_deadline}` : ""}</p>}
              </div>
            )}
            {comments.map((c) => <p key={c.id} className="mt-1 text-xs text-[#5A6B8C]"><span className="font-semibold text-[#1D4ED8]">{c.author}:</span> {c.text}</p>)}
          </div>
        );
      })}

      {/* Optional daily tasks */}
      {rev.include_tasks && snap.tasks && (
        <div className="wr-section mb-5" data-testid="wr-print-tasks">
          <h2 className="mb-2 border-b border-[#E4E9F3] pb-1 font-heading text-base font-bold text-[#06B6D4]">This Week's Tasks</h2>
          <p className="mb-2 text-xs text-[#5A6B8C]">
            Completed: <b>{snap.tasks.completed_count ?? 0}</b> · Outstanding: <b>{snap.tasks.outstanding_count ?? 0}</b> · Blocked: <b>{snap.tasks.blocked_count ?? 0}</b> · Overdue: <b>{snap.tasks.overdue_count ?? 0}</b>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#16A34A]">Completed</p>
              {(snap.tasks.completed || []).length === 0 ? <p className="text-xs text-[#5A6B8C]">None.</p> : (
                <ul className="list-disc space-y-0.5 pl-4 text-xs">{snap.tasks.completed.map((t) => (
                  <li key={t.id}><span className="font-semibold">{t.title}</span> — {t.assigned_to} · {t.workstream}</li>))}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#1D4ED8]">Outstanding</p>
              {(snap.tasks.outstanding || []).length === 0 ? <p className="text-xs text-[#5A6B8C]">None.</p> : (
                <ul className="list-disc space-y-0.5 pl-4 text-xs">{snap.tasks.outstanding.map((t) => (
                  <li key={t.id}><span className="font-semibold">{t.title}</span> — {t.assigned_to} · {t.status}{t.due_date ? ` · due ${t.due_date}` : ""}</li>))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Next-week priorities */}
      <div className="wr-section mb-5">
        <h2 className="mb-2 border-b border-[#E4E9F3] pb-1 font-heading text-base font-bold text-[#7C3AED]">Agreed Priorities for Next Week</h2>
        {(rev.next_week_priorities || []).length === 0 ? <p className="text-sm text-[#5A6B8C]">None agreed.</p> : (
          <ol className="list-decimal space-y-1.5 pl-5 text-sm">
            {rev.next_week_priorities.map((p, i) => (
              <li key={`${p.title}-${i}`}><span className="font-semibold">{p.title}</span> — {p.owner} · {p.priority}{p.due_date ? ` · due ${p.due_date}` : ""}{p.success_measure ? ` · measure: ${p.success_measure}` : ""}{p.description ? <span className="block text-[#5A6B8C]">{p.description}</span> : null}</li>
            ))}
          </ol>
        )}
      </div>

      <p className="mt-8 border-t border-[#E4E9F3] pt-3 text-center text-[10px] text-[#94A3C4]">Edju Cat War Room · Founders' Weekly Review · Snapshot generated {fmtDate(snap.generated_at)}</p>
    </div>
  );
}
